export const DEFAULT_TIMEOUT_MS = 3000;

function makeFallbackFetch(urls: string[], timeoutMs = DEFAULT_TIMEOUT_MS) {
  let cursor = 0;
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    let lastErr: unknown = null;
    for (let i = 0; i < urls.length; i++) {
      const url = urls[(cursor + i) % urls.length];

      const controller = new AbortController();
      const timer = setTimeout(() => {
        controller.abort(new Error('Request timed out'));
      }, timeoutMs);

      // If caller provides a signal, propagate its abort to our controller
      if (init?.signal) {
        if (init.signal.aborted) {
          clearTimeout(timer);
          throw init.signal.reason ?? new Error('Aborted');
        }
        init.signal.addEventListener('abort', () => {
          controller.abort(init.signal?.reason ?? new Error('Aborted'));
        });
      }

      try {
        const res = await fetch(url, { ...init, signal: controller.signal });
        clearTimeout(timer);
        if (res.ok) {
          cursor = (cursor + i) % urls.length;
          return res;
        } else {
          lastErr = new Error(`HTTP ${res.status}`);
        }
      } catch (e) {
        clearTimeout(timer);
        lastErr = e;
      }
    }
    if (lastErr) throw lastErr as any;
    throw new Error('All RPC endpoints failed');
  };
}

export const rpcFetch = (endpoints: string[], timeoutMs = DEFAULT_TIMEOUT_MS) =>
  makeFallbackFetch(endpoints, timeoutMs);

/**
 * Retries an async function up to `retries` times, with a delay between attempts.
 * @param fn The async function to execute.
 * @param retries Number of attempts (default 3).
 * @param delayMs Delay between attempts in ms (default 500).
 * @returns The result of the async function if successful.
 * @throws The last error if all attempts fail.
 */
export async function retryWithDelay<T>(
  fn: () => Promise<T>,
  retries = 3,
  delayMs = 1000,
): Promise<T> {
  let lastErr: unknown = null;
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt < retries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
  }
  throw lastErr ?? new Error('All retries failed');
}
